import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  TablePagination,
  InputAdornment,
  Box,
  Tooltip,
  LinearProgress,
  Paper,
  Typography,
  FormControlLabel,
  Checkbox
} from "@mui/material";
import { 
  Delete as DeleteIcon, 
  Add as AddIcon, 
  Search as SearchIcon 
} from "@mui/icons-material";
import axios from "axios";
import dateFormat from "dateformat";
import useDialog from "../hooks/useDialog";

const TABLE_HEADERS = [
  'საიდენტიფიკაციო კოდი',
  'ბენეფიციარი',
  'დამატების თარიღი',
  'დაამატა',
  'კომენტარი',
  'მოქმედება'
];

const TABLE_HEADERS_WITH_DELETED = [
  'საიდენტიფიკაციო კოდი',
  'ბენეფიციარი',
  'დამატების თარიღი',
  'დაამატა',
  'კომენტარი',
  'წაშლის თარიღი',
  'წაშალა',
  'მოქმედება'
];

const INITIAL_FORM_DATA = { identification_code: "", beneficiary: "", comment: "" };

const MESSAGES = {
  LOAD_ERROR: "დავალიანებების ჩატვირთვა ვერ მოხერხდა",
  ADD_SUCCESS: "დავალიანება წარმატებით დაემატა",
  ADD_ERROR: "ჩანაწერის დამატება ვერ მოხერხდა",
  DELETE_SUCCESS: "დავალიანება წარმატებით წაიშალა",
  DELETE_ERROR: "ჩანაწერის წაშლა ვერ მოხერხდა",
  VALIDATION_ERROR: "გთხოვთ შეიყვანოთ საიდენტიფიკაციო კოდი",
  NO_RECORDS: "ჩანაწერები არ მოიძებნა"
};

function DebtorRegistry() {
  const { promptConfirm, showSnackbar } = useDialog();
  
  const [data, setData] = useState({ debtors: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [includeDeleted, setIncludeDeleted] = useState(true);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  // Fetch debtors data
  const fetchDebtors = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get("/agency/debtor_registry", {
        params: { page, limit: rowsPerPage, search: debouncedSearch, includeDeleted },
      });
      setData({ debtors: res.debtors, total: res.total });
    } catch (err) {
      showSnackbar(err.response?.data?.message || MESSAGES.LOAD_ERROR, "error");
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, includeDeleted, showSnackbar]);

  useEffect(() => {
    fetchDebtors();
  }, [fetchDebtors]);

  // Generic form field handler
  const handleFormChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    if (!formData.identification_code.trim()) {
      return showSnackbar(MESSAGES.VALIDATION_ERROR, "error");
    }

    setLoading(true);
    try {
      await axios.post("/agency/debtor_registry", formData);
      showSnackbar(MESSAGES.ADD_SUCCESS, "success");
      setDialogOpen(false);
      setFormData(INITIAL_FORM_DATA);
      fetchDebtors();
    } catch (err) {
      showSnackbar(err.response?.data?.message || MESSAGES.ADD_ERROR, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await promptConfirm({
      title: "ჩანაწერის წაშლა",
      message: "გსურთ ამ ჩანაწერის წაშლა?",
    });

    if (!confirmed) return;

    setLoading(true);
    try {
      await axios.delete(`/agency/debtor_registry/${id}`);
      showSnackbar(MESSAGES.DELETE_SUCCESS, "success");
      fetchDebtors();
    } catch (err) {
      showSnackbar(err.response?.data?.message || MESSAGES.DELETE_ERROR, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setFormData(INITIAL_FORM_DATA);
  };

  const handlePageChange = (_, newPage) => setPage(newPage);

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleIncludeDeletedChange = (e) => {
    setIncludeDeleted(e.target.checked);
    setPage(0);
  };

  return (
    <Box>
      <Card variant="outlined">
        <CardHeader
          title="დავალიანებათა რეესტრი"
          titleTypographyProps={{ variant: "h5", fontWeight: 600 }}
          action={
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={includeDeleted}
                  onChange={handleIncludeDeletedChange}
                  color="primary"
                />
              }
              label="წაშლილების ჩვენება"
            />
            <TextField
              placeholder="ძებნა..."
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
              disableElevation
            >
              დამატება
            </Button>
          </Box>
        }
      />
      </Card>

      <Paper variant="outlined" sx={{ mt: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {(includeDeleted ? TABLE_HEADERS_WITH_DELETED : TABLE_HEADERS).map((header) => (
                  <TableCell key={header} sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={includeDeleted ? 8 : 6} sx={{ p: 0, border: 'none' }}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : data.debtors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={includeDeleted ? 8 : 6} align="center" sx={{ py: 3 }}>
                    <Typography color="textSecondary">{MESSAGES.NO_RECORDS}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.debtors.map((row) => (
                  <TableRow 
                    key={row._id} 
                    hover
                    sx={{
                      bgcolor: row.is_deleted ? 'rgba(255, 0, 0, 0.08)' : 'inherit',
                      '&:hover': {
                        bgcolor: row.is_deleted ? 'rgba(255, 0, 0, 0.12)' : undefined,
                      }
                    }}
                  >
                    <TableCell>{row.identification_code}</TableCell>
                    <TableCell>{row.beneficiary || "-"}</TableCell>
                    <TableCell>
                      {dateFormat(new Date(row.added_at), "dd/mm/yyyy HH:MM")}
                    </TableCell>
                    <TableCell>{row.added_by?.name || "N/A"}</TableCell>
                    <TableCell>{row.comment || "-"}</TableCell>
                    {includeDeleted && (
                      <>
                        <TableCell>
                          {row.is_deleted && row.deleted_at
                            ? dateFormat(new Date(row.deleted_at), "dd/mm/yyyy HH:MM")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {row.is_deleted && row.deleted_by?.name ? row.deleted_by.name : "-"}
                        </TableCell>
                      </>
                    )}
                    <TableCell>
                      {!row.is_deleted && (
                        <Tooltip title="წაშლა">
                          <IconButton 
                            color="error" 
                            size="small" 
                            onClick={() => handleDelete(row._id)}
                            disabled={loading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={data.total}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          labelRowsPerPage="ჩანაწერები:"
          sx={{ borderTop: 1, borderColor: 'divider' }}
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>ჩანაწერის დამატება</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              autoFocus
              label="საიდენტიფიკაციო კოდი"
              fullWidth
              value={formData.identification_code}
              onChange={handleFormChange('identification_code')}
            />
            <TextField
              label="ბენეფიციარი"
              fullWidth
              value={formData.beneficiary}
              onChange={handleFormChange('beneficiary')}
            />
            <TextField
              label="კომენტარი"
              fullWidth
              multiline
              rows={3}
              value={formData.comment}
              onChange={handleFormChange('comment')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>გაუქმება</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            შენახვა
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DebtorRegistry;
